import { PostLink } from "../../../../../stores/CommunityStore/posts/types";
import { classifyUrl } from "../Composer/classifyUrl";
import { toResolvedLinkAttachment } from "../Composer/eagerLinkFetch";
import { siteName } from "../Composer/siteName";
import { LinkAttachment } from "../Composer/types";
import { LinkMetadata } from "./types";

const hostnameFromUrl = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

// Normalizes the server's legacy unfurl result (LinkMetadata, the
// post-level `metadata` column) into a LinkAttachment. Legacy posts only —
// posts with community_post_links rows render via postLinkToAttachment
// below instead. Returns null when the unfurl produced nothing at all (no
// URL) — there's nothing to show.
export const linkAttachmentFromMetadata = (
  metadata: LinkMetadata,
): LinkAttachment | null => {
  if (!metadata.url) return null;

  const domain = metadata.hostname || hostnameFromUrl(metadata.url);
  const resolved = Boolean(metadata.title || metadata.image);

  return {
    id: "post-link",
    kind: "link",
    url: metadata.url,
    domain,
    favicon: metadata.favicon,
    image: metadata.image,
    title: metadata.title,
    description: metadata.description,
    status: resolved ? "resolved" : "bare",
    site: resolved ? undefined : siteName(domain),
  };
};

// Normalizes a community_post_links row into a LinkAttachment for display
// (or for pre-filling the edit form). Never produces "loading" — a saved
// post's link is either resolved or it isn't; only newly-added links in an
// open composer go through the loading state.
export const postLinkToAttachment = (link: PostLink): LinkAttachment => {
  const classified = classifyUrl(link.url) || {
    url: link.url,
    kind: "link" as const,
    domain: hostnameFromUrl(link.url),
  };

  return toResolvedLinkAttachment(
    {
      id: `link-${link.id}`,
      kind: classified.kind,
      url: link.url,
      domain: classified.domain,
      videoId: classified.videoId,
    },
    link.status === "resolved" ? link.metadata : null,
  );
};
